import { useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { NotificationEvent } from '../../types/notifications.types';
import { getNotificationDisplay } from './notification-display';
import { useSocialNotifications } from '../../hooks/useSocialNotifications';

/** Relative "5m ago" timestamp — no date lib in the repo, so inline. */
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

/**
 * Human one-liner per social event. `tna_negative` gets a bespoke
 * sentence; any other social type falls back to its display label plus
 * the pair name. Defensive against an unshaped `payload`.
 */
function socialMessage(evt: NotificationEvent): string {
  const display = getNotificationDisplay(evt.type);
  const payload = (evt.payload ?? {}) as Record<string, unknown>;

  if (evt.type === 'tna_negative') {
    const tna = payload.tna;
    return `TNA went negative${
      evt.pairName ? ` on ${evt.pairName}` : ''
    }${tna != null ? ` (${String(tna)})` : ''}`;
  }

  const label = display.label ?? evt.type;
  return evt.pairName ? `${label} — ${evt.pairName}` : label;
}

export function NotificationBell() {
  const { items, unreadCount, markAllRead } = useSocialNotifications();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Capture which items were unread at open time so the popover can dot
  // them even though `markAllRead` fires immediately on open.
  const [unreadIdsAtOpen, setUnreadIdsAtOpen] = useState<Set<string>>(
    () => new Set(),
  );

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setUnreadIdsAtOpen(new Set(items.slice(0, unreadCount).map((i) => i.id)));
    setAnchorEl(event.currentTarget);
    markAllRead();
  };

  const handleClose = () => setAnchorEl(null);

  const list = useMemo(() => items, [items]);

  return (
    <>
      <Tooltip title="Notifications" placement="right">
        <IconButton
          onClick={handleOpen}
          size="small"
          sx={{
            color: (theme) => theme.palette.grey[600],
            '& > svg': {
              fontSize: '1.1rem',
              '&:hover': {
                color: (theme) => theme.palette.primary.main,
              },
            },
          }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsNoneOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ width: 360, maxHeight: 480, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: 'common.white' }}
            >
              Notifications
            </Typography>
          </Box>
          <Divider />

          {list.length === 0 ? (
            <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          ) : (
            <Stack sx={{ overflowY: 'auto' }} divider={<Divider />}>
              {list.map((evt) => {
                const display = getNotificationDisplay(evt.type);
                const title = display.label ?? evt.type;
                const isUnread = unreadIdsAtOpen.has(evt.id);
                return (
                  <Box
                    key={evt.id}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      px: 2,
                      py: 1.5,
                      alignItems: 'flex-start',
                      backgroundColor: isUnread
                        ? 'action.hover'
                        : 'transparent',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: '1.1rem',
                        bgcolor: `${display.color}22`,
                      }}
                    >
                      {display.emoji}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700 }}
                          noWrap
                        >
                          {title}
                        </Typography>
                        {isUnread && (
                          <Box
                            component="span"
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              bgcolor: 'error.main',
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ wordBreak: 'break-word' }}
                      >
                        {socialMessage(evt)}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {timeAgo(evt.occurredAt)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Popover>
    </>
  );
}
